import React, { useState, useMemo } from 'react';
import './BillingStatement.scss';

// Імпорт SVG іконок як статичних ресурсів
import visaIcon from '@/img/visa.svg';
import mastercardIcon from '@/img/mastercard.svg';
import applePayIcon from '@/img/applePay.svg';
import googlePayIcon from '@/img/googlePay.svg';
import pciDssBadge from '@/img/pci_dss_badge.svg';
import checkboxIcon from '@/img/checkbox.svg';
import checkboxSelectedIcon from '@/img/checkbox-selected.svg';

// Типи даних
interface CustomerInfo {
  name: string;
  address: string;
  unit: string;
  phone: string;
}

interface PaymentItem {
  description: string;
  amount: number;
}

interface PaymentMethod {
  id: string;
  type: 'visa' | 'mastercard' | 'applepay' | 'googlepay' | 'ach';
  label: string;
  lastFour?: string;
}

interface BillingStatementData {
  customer: CustomerInfo;
  balanceDue: number;
  dueInDays: number;
  paymentItems: PaymentItem[];
  total: number;
  paymentMethods: PaymentMethod[];
  defaultPaymentMethodId: string;
}

interface BillingStatementProps {
  data?: BillingStatementData;
  onBack?: () => void;
  onExportPDF?: () => void;
  onPayment?: (amount: number, paymentMethodId: string, options: PaymentOptions) => void;
  overrideAmount?: number; // Змінено з number | null на number
}

interface PaymentOptions {
  isRecurring: boolean;
  receiptDelivery: {
    email: boolean;
    sms: boolean;
  };
  authorizeStorage: boolean;
  agreeToTerms: boolean;
}

// Моковые данные
const mockData: BillingStatementData = {
  customer: {
    name: 'Frank Mitchell',
    address: '314 Rue de Montceau, Laval, QC H7N 1E9, Canada',
    unit: '203',
    phone: '+12505550199'
  },
  balanceDue: 1420.19,
  dueInDays: 3,
  paymentItems: [
    { description: 'Rent', amount: 1200.00 },
    { description: 'Utilities', amount: 0.00 },
    { description: 'Late Fees', amount: 220.19 }
  ],
  total: 1420.19,
  paymentMethods: [
    { id: 'visa-3467', type: 'visa', label: 'Visa', lastFour: '3467' },
    { id: 'mastercard-7284', type: 'mastercard', label: 'Mastercard', lastFour: '7284' },
    { id: 'applepay', type: 'applepay', label: 'ApplePay' },
    { id: 'googlepay', type: 'googlepay', label: 'GooglePay' },
    { id: 'ach', type: 'ach', label: 'ACH/Bank Account' }
  ],
  defaultPaymentMethodId: 'visa-3467'
};

// Заглушки для функций
const defaultHandlers = {
  onBack: () => console.log('Back clicked'),
  onExportPDF: () => console.log('Export PDF clicked'),
  onPayment: (amount: number, paymentMethodId: string, options: PaymentOptions) =>
    console.log('Payment:', { amount, paymentMethodId, options })
};

const BillingStatement: React.FC<BillingStatementProps> = ({
                                                             data = mockData,
                                                             onBack = defaultHandlers.onBack,
                                                             onExportPDF = defaultHandlers.onExportPDF,
                                                             onPayment = defaultHandlers.onPayment,
                                                             overrideAmount
                                                           }) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(data.defaultPaymentMethodId);
  const [isRecurring, setIsRecurring] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState(true);
  const [receiptSMS, setReceiptSMS] = useState(false);
  const [authorizeStorage, setAuthorizeStorage] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Обчислюємо дані з урахуванням перевизначеної суми
  const displayData = useMemo(() => {
    if (!overrideAmount) {
      return data;
    }

    // Створюємо новий об'єкт з перевизначеною сумою
    const newPaymentItems = data.paymentItems.map((item, index) => {
      if (index === 0) { // Перший елемент (зазвичай Rent)
        return { ...item, amount: overrideAmount };
      }
      return { ...item, amount: 0 }; // Інші елементи обнуляємо
    });

    return {
      ...data,
      balanceDue: overrideAmount,
      total: overrideAmount,
      paymentItems: newPaymentItems
    };
  }, [data, overrideAmount]);

  // SVG компоненти для способів оплати
  const VisaIcon: React.FC = () => (
    <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="0.5" width="34" height="23" rx="3.5" fill="white" stroke="#D9D9D9"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M8.8462 8.93296C9.31624 8.97261 9.78628 8.69506 10.0801 8.34316C10.3689 7.98135 10.5599 7.49563 10.5109 7C10.0947 7.01983 9.58064 7.27755 9.28686 7.63936C9.01757 7.95161 8.78745 8.45715 8.8462 8.93296ZM14.4377 15.3167V7.58981H17.3021C18.7807 7.58981 19.8138 8.62072 19.8138 10.1274C19.8138 11.6342 18.7611 12.675 17.2629 12.675H15.6226V15.3167H14.4377ZM10.506 9.02714C10.0919 9.00301 9.71406 9.1532 9.40886 9.27451C9.21246 9.35257 9.04614 9.41868 8.91961 9.41868C8.77762 9.41868 8.60444 9.34904 8.40999 9.27085C8.15521 9.16839 7.86392 9.05125 7.55845 9.05687C6.85829 9.06679 6.20709 9.46825 5.84966 10.1076C5.11522 11.3863 5.6587 13.2796 6.36866 14.3205C6.7163 14.8359 7.13248 15.4009 7.68086 15.3811C7.92211 15.3719 8.09566 15.2974 8.27526 15.2202C8.48203 15.1314 8.69683 15.0391 9.03223 15.0391C9.35599 15.0391 9.5614 15.129 9.75858 15.2152C9.94606 15.2973 10.1261 15.376 10.3934 15.3712C10.9614 15.3613 11.3188 14.8557 11.6664 14.3403C12.0416 13.7871 12.2064 13.2471 12.2314 13.1652L12.2344 13.1557C12.2338 13.1551 12.2291 13.153 12.221 13.1492C12.0956 13.0911 11.137 12.6469 11.1278 11.4557C11.1186 10.4559 11.8881 9.94937 12.0093 9.86963L12.0093 9.86962C12.0166 9.86477 12.0216 9.8615 12.0238 9.85979C11.5342 9.12626 10.7704 9.04696 10.506 9.02714ZM21.978 15.3761C22.7223 15.3761 23.4126 14.9945 23.726 14.3898H23.7505V15.3166H24.8472V11.4706C24.8472 10.3554 23.9659 9.63672 22.6096 9.63672C21.3513 9.63672 20.421 10.3653 20.3867 11.3665H21.4541C21.5423 10.8907 21.978 10.5784 22.5754 10.5784C23.3 10.5784 23.7064 10.9204 23.7064 11.5499V11.9761L22.2277 12.0653C20.8519 12.1496 20.1077 12.7195 20.1077 13.7108C20.1077 14.712 20.8764 15.3761 21.978 15.3761ZM22.2962 14.4592C21.6646 14.4592 21.2631 14.1519 21.2631 13.6811C21.2631 13.1954 21.6499 12.9129 22.3893 12.8683L23.7063 12.784V13.2202C23.7063 13.9438 23.0992 14.4592 22.2962 14.4592ZM28.4851 15.619C28.0102 16.9721 27.4667 17.4181 26.3112 17.4181C26.2231 17.4181 25.9293 17.4082 25.8607 17.3884V16.4616C25.9342 16.4715 26.1153 16.4814 26.2084 16.4814C26.7323 16.4814 27.026 16.2584 27.2072 15.6785L27.3149 15.3365L25.3075 9.71108H26.5462L27.9416 14.2758H27.9661L29.3616 9.71108H30.566L28.4851 15.619ZM15.6225 8.60087H16.9886C18.0168 8.60087 18.6044 9.15598 18.6044 10.1324C18.6044 11.1088 18.0168 11.6688 16.9837 11.6688H15.6225V8.60087Z" fill="black"/>
    </svg>
  );

  const MastercardIcon: React.FC = () => (
    <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="0.5" width="34" height="23" rx="3.5" fill="white" stroke="#D9D9D9"/>
      {/* Здесь нужен SVG код для Mastercard */}
      <text x="18" y="15" textAnchor="middle" fontSize="8" fill="#666">MC</text>
    </svg>
  );

  const ApplePayIcon: React.FC = () => (
    <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="0.5" width="34" height="23" rx="3.5" fill="white" stroke="#D9D9D9"/>
      {/* Здесь нужен SVG код для Apple Pay */}
      <text x="18" y="15" textAnchor="middle" fontSize="7" fill="#666">Pay</text>
    </svg>
  );

  const GooglePayIcon: React.FC = () => (
    <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="0.5" width="34" height="23" rx="3.5" fill="white" stroke="#D9D9D9"/>
      {/* Здесь нужен SVG код для Google Pay */}
      <text x="18" y="15" textAnchor="middle" fontSize="6" fill="#666">GPay</text>
    </svg>
  );

  const PCIDSSBadge: React.FC = () => (
    <svg width="126" height="48" viewBox="0 0 126 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Здесь нужен SVG код для PCI DSS badge */}
      <rect width="126" height="48" rx="4" fill="#225E63"/>
      <text x="63" y="28" textAnchor="middle" fontSize="12" fill="white" fontWeight="600">PCI DSS</text>
    </svg>
  );

  const CheckboxIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="15" height="15" rx="3.5" fill="white" stroke="#C4CDD5"/>
    </svg>
  );

  const CheckboxSelectedIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="16" height="16" rx="4" fill="#105E96"/>
      <path d="M4.5 8L7 10.5L11.5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const handlePayment = () => {
    if (!agreeToTerms) {
      alert('Please agree to Terms of Service and Late Fee Policy');
      return;
    }

    const options: PaymentOptions = {
      isRecurring,
      receiptDelivery: {
        email: receiptEmail,
        sms: receiptSMS
      },
      authorizeStorage,
      agreeToTerms
    };

    // Використовуємо фактичну суму для відображення
    onPayment(displayData.total, selectedPaymentMethod, options);
  };

  // SVG иконки
  const DownloadIcon: React.FC = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.979 19.804 4.587 19.412C4.195 19.02 3.99934 18.5493 4 18V15H6V18H18V15H20V18C20 18.55 19.804 19.021 19.412 19.413C19.02 19.805 18.5493 20.0007 18 20H6Z" fill="white"/>
    </svg>
  );

  const AddIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1V15M1 8H15" stroke="#105E96" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const renderPaymentMethodIcon = (method: PaymentMethod) => {
    const iconMap = {
      visa: visaIcon,
      mastercard: mastercardIcon,
      applepay: applePayIcon,
      googlepay: googlePayIcon,
      ach: null
    };

    const iconSrc = iconMap[method.type];

    if (!iconSrc) {
      return (
        <div className="BillingStatement__payment-icon BillingStatement__payment-icon--text">
          BANK
        </div>
      );
    }

    return (
      <div className="BillingStatement__payment-icon">
        <img src={iconSrc} alt={method.label} />
      </div>
    );
  };

  return (
    <div className="BillingStatement">
      {/* Page Title */}
      <div className="BillingStatement__page-title">
        <div className="BillingStatement__title-content">
          <h1 className="BillingStatement__title">Billing Statement</h1>
          <div className="BillingStatement__buttons">
            <button
              className="BillingStatement__button BillingStatement__button--outline"
              onClick={onBack}
            >
              Back
            </button>
            <button
              className="BillingStatement__button BillingStatement__button--primary"
              onClick={onExportPDF}
            >
              <DownloadIcon />
              <span>Export as PDF</span>
            </button>
          </div>
        </div>
        <div className="BillingStatement__line"></div>
      </div>

      {/* Content */}
      <div className="BillingStatement__content">
        {/* Info Section */}
        <div className="BillingStatement__info">
          <div className="BillingStatement__bill-to">
            <h2 className="BillingStatement__bill-title">Bill to:</h2>
            <div className="BillingStatement__vertical-line"></div>
            <div className="BillingStatement__customer-table">
              <div className="BillingStatement__field-names">
                <div className="BillingStatement__field-name">Name:</div>
                <div className="BillingStatement__field-name">Address:</div>
                <div className="BillingStatement__field-name">Unit:</div>
                <div className="BillingStatement__field-name">Phone:</div>
              </div>
              <div className="BillingStatement__field-infos">
                <div className="BillingStatement__field-info">{displayData.customer.name}</div>
                <div className="BillingStatement__field-info">{displayData.customer.address}</div>
                <div className="BillingStatement__field-info">{displayData.customer.unit}</div>
                <div className="BillingStatement__field-info">{displayData.customer.phone}</div>
              </div>
            </div>
          </div>

          <div className="BillingStatement__balance-block">
            <div className="BillingStatement__balance-table">
              <div className="BillingStatement__field-names">
                <div className="BillingStatement__field-name BillingStatement__field-name--gray">Balance Due:</div>
                <div className="BillingStatement__field-name BillingStatement__field-name--gray">Due in:</div>
              </div>
              <div className="BillingStatement__field-infos">
                <div className="BillingStatement__field-info BillingStatement__field-info--amount">
                  ${displayData.balanceDue.toFixed(2)}
                </div>
                <div className="BillingStatement__field-info BillingStatement__field-info--bold">
                  {displayData.dueInDays} days
                </div>
              </div>
            </div>
            {/* PCI DSS Badge */}
            <div className="BillingStatement__pci-badge">
              <img src={pciDssBadge} alt="PCI DSS Compliant" />
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="BillingStatement__details">
          <div className="BillingStatement__section-title">
            <h3 className="BillingStatement__section-heading">Payment Summary</h3>
            <div className="BillingStatement__section-line"></div>
          </div>

          <div className="BillingStatement__payment-table">
            <div className="BillingStatement__table-description">
              <div className="BillingStatement__table-header">
                <div className="BillingStatement__table-header-cell">Description</div>
              </div>
              {displayData.paymentItems.map((item, index) => (
                <div key={index} className="BillingStatement__table-cell">
                  <div className="BillingStatement__table-cell-text">{item.description}</div>
                </div>
              ))}
              <div className="BillingStatement__table-total">
                <div className="BillingStatement__table-total-text">TOTAL</div>
              </div>
            </div>

            <div className="BillingStatement__table-amount">
              <div className="BillingStatement__table-header">
                <div className="BillingStatement__table-header-cell">Amount</div>
              </div>
              {displayData.paymentItems.map((item, index) => (
                <div key={index} className="BillingStatement__table-cell">
                  <div className="BillingStatement__table-amount-text">
                    $ {item.amount.toFixed(2)}
                  </div>
                </div>
              ))}
              <div className="BillingStatement__table-total">
                <div className="BillingStatement__table-total-amount">
                  $ {displayData.total.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="BillingStatement__method">
          <div className="BillingStatement__section-title">
            <h3 className="BillingStatement__section-heading">Payment Method</h3>
            <div className="BillingStatement__section-line"></div>
          </div>

          <div className="BillingStatement__payment-methods">
            {displayData.paymentMethods.map((method) => (
              <div key={method.id} className="BillingStatement__payment-column">
                <div className="BillingStatement__payment-option">
                  <div className="BillingStatement__radio-field">
                    <div
                      className={`BillingStatement__radiobutton ${
                        selectedPaymentMethod === method.id ? 'BillingStatement__radiobutton--selected' : ''
                      }`}
                      onClick={() => setSelectedPaymentMethod(method.id)}
                    >
                      {selectedPaymentMethod === method.id && (
                        <div className="BillingStatement__radio-dot"></div>
                      )}
                    </div>
                    {renderPaymentMethodIcon(method)}
                    <span className="BillingStatement__payment-label">
                      {method.lastFour ? `****${method.lastFour}` : method.label}
                    </span>
                  </div>
                </div>
                {method.id === 'ach' && (
                  <div className="BillingStatement__add-new">
                    <AddIcon />
                    <span className="BillingStatement__add-text">Add new method</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="BillingStatement__method-line"></div>

          {/* Payment Options */}
          <div className="BillingStatement__payment-actions">
            <div className="BillingStatement__toggle-group">
              <span className="BillingStatement__toggle-label">Recurring payment</span>
              <div
                className={`BillingStatement__toggle ${isRecurring ? 'BillingStatement__toggle--active' : ''}`}
                onClick={() => setIsRecurring(!isRecurring)}
              >
                <div className="BillingStatement__toggle-switch"></div>
              </div>
            </div>

            <div className="BillingStatement__receipt-group">
              <span className="BillingStatement__receipt-label">Receipt delivery</span>
              <div className="BillingStatement__checkbox-group">
                <div className="BillingStatement__checkbox-field">
                  <div
                    className="BillingStatement__checkbox"
                    onClick={() => setReceiptEmail(!receiptEmail)}
                  >
                    <img
                      src={receiptEmail ? checkboxSelectedIcon : checkboxIcon}
                      alt="checkbox"
                    />
                  </div>
                  <span>Email</span>
                </div>
                <div className="BillingStatement__checkbox-field">
                  <div
                    className="BillingStatement__checkbox"
                    onClick={() => setReceiptSMS(!receiptSMS)}
                  >
                    <img
                      src={receiptSMS ? checkboxSelectedIcon : checkboxIcon}
                      alt="checkbox"
                    />
                  </div>
                  <span>SMS</span>
                </div>
              </div>
            </div>
          </div>

          <div className="BillingStatement__method-line"></div>

          {/* Agreement Checkboxes */}
          <div className="BillingStatement__agreements">
            <div className="BillingStatement__checkbox-field">
              <div
                className="BillingStatement__checkbox"
                onClick={() => setAuthorizeStorage(!authorizeStorage)}
              >
                <img
                  src={authorizeStorage ? checkboxSelectedIcon : checkboxIcon}
                  alt="checkbox"
                />
              </div>
              <span className="BillingStatement__agreement-text">
                I authorize PropSavvy to store my payment method per PCI-DSS guidelines.
              </span>
            </div>

            <div className="BillingStatement__checkbox-field">
              <div
                className="BillingStatement__checkbox"
                onClick={() => setAgreeToTerms(!agreeToTerms)}
              >
                <img
                  src={agreeToTerms ? checkboxSelectedIcon : checkboxIcon}
                  alt="checkbox"
                />
              </div>
              <span className="BillingStatement__agreement-text">
                I agree to <span className="BillingStatement__link">Terms of Service</span> and <span className="BillingStatement__link">Late Fee Policy</span>.
              </span>
            </div>
          </div>

          {/* Pay Button */}
          <div className="BillingStatement__pay-button-container">
            <button
              className="BillingStatement__pay-button"
              onClick={handlePayment}
              disabled={!agreeToTerms}
            >
              Pay ${displayData.total.toFixed(2)}
            </button>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="BillingStatement__help">
        <div className="BillingStatement__help-line"></div>
        <div className="BillingStatement__help-text">
          Need help? Contact our <span className="BillingStatement__link">support</span>!
        </div>
      </div>
    </div>
  );
};

export default BillingStatement;